/**
 * Chat tab registry (issue #351).
 *
 * The shared-substrate contract that lets #761's future AI tabs
 * (agent_navigator / agent_reader) plug into the same widget shell
 * without touching the launcher or panel code. v1 registers exactly
 * one tab — Techsupport.
 *
 * IMPORTANT: this contract is *frozen* once #351 ships. Future tabs
 * must conform to `ChatTabDescriptor`; adding/changing fields is a
 * breaking change.
 */
import type { ReactNode } from 'react';

export interface ChatTabDescriptor {
  /** Stable identifier (e.g. ``techsupport``, ``agent_navigator``). */
  id: string;
  /** Visible label (i18n key resolved at render time). */
  label: string;
  /** Optional unread badge count; hidden if undefined / 0. */
  badgeCount?: number;
  /** Render the tab body when active. */
  render: () => ReactNode;
}

const _registry: ChatTabDescriptor[] = [];

export function registerChatTab(descriptor: ChatTabDescriptor): void {
  const existing = _registry.findIndex((t) => t.id === descriptor.id);
  if (existing >= 0) {
    _registry[existing] = descriptor;
    return;
  }
  _registry.push(descriptor);
}

export function unregisterChatTab(id: string): void {
  const idx = _registry.findIndex((t) => t.id === id);
  if (idx >= 0) _registry.splice(idx, 1);
}

export function getChatTabs(): ChatTabDescriptor[] {
  return [..._registry];
}

/** For tests only — reset the registry between test cases. */
export function _resetChatTabRegistryForTests(): void {
  _registry.length = 0;
}
